/**
 * Who may edit a limit cell, and the hints of the cell edit (masterplan credit-limits-v3, B4; E7, E11, E19, E20).
 * Pure; the server decides again (`workflow.ts` `canEditLimit`, 403 / 409).
 *
 * - E7: the customer's salesperson and his BT manager (by the approver rows: `roles.smFor` / `roles.btmFor` name the
 *   `SalesManagerName` values, compared exactly as `canWriteComments` and «My customers» do) and Finance at every
 *   customer, also one without a salesperson.
 * - E11: an open request (pending, ready, writing) locks the cell until it is decided or withdrawn.
 * - Until migration 227 the overview says `requestsAvailable: false`: no cell is editable.
 */
import type { CreditLimitCallerRoles, OpenRequestSummary, OverviewRow } from '../../../types/creditLimits';

export type LimitEditBlock = 'no_requests' | 'no_role' | 'open_request';

export function limitEditBlock(
    row: Pick<OverviewRow, 'salesManagerName' | 'openRequest'>,
    roles: Pick<CreditLimitCallerRoles, 'finance' | 'smFor' | 'btmFor'> | null,
    requestsAvailable: boolean,
): LimitEditBlock | null {
    if (!requestsAvailable) return 'no_requests';
    if (!roles) return 'no_role';
    const sm = row.salesManagerName;
    const ownRole = roles.finance || (sm !== null && (roles.smFor.includes(sm) || roles.btmFor.includes(sm)));
    if (!ownRole) return 'no_role';
    if (row.openRequest) return 'open_request';
    return null;
}

/** The tooltip and accessible name of an editable limit cell (Roman, 2026-10-23). */
export const EDIT_TITLE = 'Change limit – creates a request';
export const LOCKED_TITLE = 'An open request locks this limit until it is decided or withdrawn';

/**
 * E19: a hint (never a block) when the requested limit is above the credit insurance limit. Most customers carry 0
 * («no insurance», 5'985 of 6'253 rows on 2026-10-23), so the hint only speaks where an insurance limit exists. The
 * insurance is in EUR, the limit in CHF; the two figures are compared as they are, without a rate, and the hint names
 * both currencies.
 */
export function insuranceHint(requestedChf: number, insuranceEur: number | null): string | null {
    if (insuranceEur === null || !(insuranceEur > 0)) return null;
    if (!(requestedChf > insuranceEur)) return null;
    return `The new limit (CHF ${requestedChf.toLocaleString('de-CH')}) is above the credit insurance limit (EUR ${Math.round(insuranceEur).toLocaleString('de-CH')}).`;
}

/** The overview's open request of a customer, local ones (just sent, not yet reloaded) first. */
export function withLocalRequests(rows: readonly OverviewRow[], local: ReadonlyMap<string, OpenRequestSummary>): readonly OverviewRow[] {
    if (local.size === 0) return rows;
    return rows.map(r => (r.openRequest === null && local.has(r.customerCode) ? { ...r, openRequest: local.get(r.customerCode)! } : r));
}
